import React, { useState } from "react";
import {
  Steps,
  Input,
  Button,
  notification,
  Form,
} from "antd";
import api from "../../services/api";
import { useFreire } from "../../utils/freireContext";
import * as texts from "./locales";

const { Step } = Steps;

const APPNAME_HEADER = { headers: { appname: localStorage.getItem("companyURL") || "tupinamba" } };

const PasswordReset = ({ initialEmail = "", onBackToLogin }) => {
  const { freire } = useFreire();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendCode = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/requestPasswordReset/", { email }, APPNAME_HEADER);
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.CODE_SENT_SUCCESS),
      });
      setStep(1);
    } catch {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.CHECK_AUTH),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code) return;
    setLoading(true);
    try {
      await api.get("/requestPasswordReset/", {
        ...APPNAME_HEADER,
        params: { email, code },
      });
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.CODE_VERIFIED),
      });
      setStep(2);
    } catch {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.INVALID_CODE),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) return;
    if (password !== confirmPassword) {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.PASSWORDS_DO_NOT_MATCH),
      });
      return;
    }
    if (password.length < 6) {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.PASSWORD_MIN_LENGTH),
      });
      return;
    }
    setLoading(true);
    try {
      await api.put("/requestPasswordReset/", { password, email, code }, APPNAME_HEADER);
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.PASSWORD_RESET_SUCCESS),
      });
      onBackToLogin(email);
    } catch {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.CHECK_AUTH),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await api.post("/requestPasswordReset/", { email }, APPNAME_HEADER);
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.CODE_SENT_SUCCESS),
      });
    } catch {
      notification.error({
        message: freire(texts.WENT_WONG),
        description: freire(texts.CHECK_AUTH),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 24 }}
      >
        <Step title={freire(texts.STEP_EMAIL_TITLE)} />
        <Step title={freire(texts.STEP_CODE_TITLE)} />
        <Step title={freire(texts.STEP_PASSWORD_TITLE)} />
      </Steps>

      {step === 0 && (
        <Form>
          <p style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)" }}>
            {freire(texts.ENTER_YOUR_EMAIL)}
          </p>
          <Form.Item>
            <Input
              type="email"
              size="large"
              placeholder={freire(texts.EMAIL)}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={handleSendCode}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              loading={loading}
              onClick={handleSendCode}
              style={{ width: "100%", backgroundColor: "#001529" }}
            >
              {freire(texts.SEND_CODE)}
            </Button>
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button type="link" onClick={() => onBackToLogin()}>
              {freire(texts.BACK_TO_LOGIN)}
            </Button>
          </div>
        </Form>
      )}

      {step === 1 && (
        <Form>
          <p style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)" }}>
            {freire(texts.ENTER_CODE)} <strong>{email}</strong>
          </p>
          <Form.Item>
            <Input
              size="large"
              placeholder={freire(texts.CODE_LABEL)}
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onPressEnter={handleVerifyCode}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              loading={loading}
              onClick={handleVerifyCode}
              style={{ width: "100%", backgroundColor: "#001529" }}
            >
              {freire(texts.VERIFY_CODE)}
            </Button>
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button type="link" onClick={handleResendCode} disabled={loading}>
              {freire(texts.RESEND_CODE)}
            </Button>
            {" · "}
            <Button type="link" onClick={() => setStep(0)}>
              {freire(texts.BACK_TO_LOGIN)}
            </Button>
          </div>
        </Form>
      )}

      {step === 2 && (
        <Form>
          <Form.Item>
            <Input.Password
              size="large"
              placeholder={freire(texts.NEW_PASSWORD)}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Input.Password
              size="large"
              placeholder={freire(texts.CONFIRM_PASSWORD)}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onPressEnter={handleResetPassword}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              loading={loading}
              onClick={handleResetPassword}
              style={{ width: "100%", backgroundColor: "#001529" }}
            >
              {freire(texts.RESET_PASSWORD_BTN)}
            </Button>
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button type="link" onClick={() => setStep(1)}>
              {freire(texts.BACK_TO_LOGIN)}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default PasswordReset;
